import type { JSX } from 'react';
import React, { useMemo } from 'react';

import type { WithSlotProps } from '~/components/hocs';
import { withSlot } from '~/components/hocs';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { cn } from '~/utils/cn';
import { renderChild } from '../utils';

export const NAME = 'TextInput';

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
  id?: string;
  name: string;
  value: string;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  touched: boolean;
  errors?: string[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const TextInputComponent = (props: Props) => {
  const { id, name, value, label, placeholder, autoFocus, className, errors, onChange, onBlur, onFocus } = props;
  const template = useTemplate(NAME);
  const slots = useMemo(
    () => ({
      container: {
        className: cn(className, errors?.length && 'with-error'),
      } satisfies JSX.IntrinsicElements['div'],

      label: {
        htmlFor: id,
      } satisfies JSX.IntrinsicElements['label'],

      labelText: {
        children: label ?? null,
      } satisfies JSX.IntrinsicElements['span'],

      inputWrap: {} satisfies JSX.IntrinsicElements['div'],

      input: {
        id,
        name,
        value,
        placeholder,
        autoFocus,
        onChange,
        onBlur,
        onFocus,
      } satisfies JSX.IntrinsicElements['input'],

      errors: {
        children: errors?.map((error, index) => (
          <li key={error}>
            {error}
            {index !== errors.length - 1 && ','}
          </li>
        )),
      } satisfies JSX.IntrinsicElements['div'],
    }),
    [id, name, value, label, placeholder, autoFocus, className, errors, onChange, onBlur, onFocus]
  );

  return <SlotsProvider slots={slots}> {props.children ?? renderChild(template)}</SlotsProvider>;
};

TextInputComponent.displayName = NAME;

export const TextInput = withSlot(TextInputComponent);
